/**
 * The workflow-demo enquiry: the step an installer can take before committing
 * to the presale. One place for the promise, so the form, the success state,
 * the emails and the FAQ never disagree about what happens next or how fast.
 */
export const RESPONSE_TIME = 'two working days';

export const demo = {
	/** The site-wide positioning line. Keep page headlines consistent with it. */
	positioning: 'Create the PPF pattern your existing library doesn’t have, from your own 3D scan.',
	label: 'Workflow demo',
	cta: 'Request a workflow demo',
	heading: 'Request a workflow demo for your missing-pattern job.',
	intro:
		'Tell us the vehicle or panel your pattern library does not cover, and what you scan and cut with. You see the real software run on a job like yours before you decide anything.',
	/** Shown beside the form and repeated in the confirmation email. */
	steps: [
		'You get a confirmation email straight away, so you know it arrived.',
		`A person replies within ${RESPONSE_TIME} (Estonia, Monday to Friday) with a demo on a comparable panel: a recorded walkthrough or a time for a live screen share.`,
		'We tell you honestly whether your scanner, plotter and panel are a good fit today.',
		'No payment, no sales sequence. Your details are used for this demo only.'
	],
	fields: {
		email: { label: 'Work email', placeholder: 'you@shop.com' },
		vehiclePanel: {
			label: 'Vehicle and panel you cannot get a pattern for',
			placeholder: '2026 Ram 1500 fender flare'
		},
		scanner: {
			label: '3D scanner (or "none yet")',
			placeholder: 'Einstar Rockit, Revopoint, Artec…'
		},
		plotter: { label: 'Plotter', placeholder: 'Graphtec FC9000, Summa S3…' }
	},
	success: {
		title: 'Request received',
		body: `Check your inbox for the confirmation. A person replies within ${RESPONSE_TIME}.`
	},
	errors: {
		email: 'That email looks incomplete.',
		vehiclePanel: 'Tell us the vehicle or panel, so the demo fits your job.',
		generic: 'Something went wrong. Try again, or email hello@ppfengine.com.',
		network: 'Network error. Try again.'
	}
} as const;

export interface DemoRequestDetails {
	email: string;
	vehiclePanel: string;
	scanner: string;
	plotter: string;
	source: string;
}

/** Confirmation to the installer: repeats the promise made on the page. */
export function demoRequestConfirmationEmail(details: DemoRequestDetails) {
	return {
		subject: 'Your PPF Engine workflow demo request',
		text: [
			`We have your request for a workflow demo.`,
			``,
			`Vehicle / panel: ${details.vehiclePanel}`,
			`Scanner: ${details.scanner || 'not given'}`,
			`Plotter: ${details.plotter || 'not given'}`,
			``,
			`What happens next: a person replies to this address within ${RESPONSE_TIME} with a demo on a comparable panel, either a recorded walkthrough or a time for a live screen share. We will also tell you plainly whether your equipment and panel are a good fit today.`,
			``,
			`Anything to add (photos, a scan file, a deadline)? Reply to this email.`,
			``,
			`If this wasn’t you, ignore this email and nothing happens.`
		].join('\n')
	};
}

/** Internal notification: everything needed to reply without opening the admin UI. */
export function demoRequestNotificationEmail(
	details: DemoRequestDetails,
	attribution: Record<string, string>
) {
	const attributionLines = Object.entries(attribution).map(([key, value]) => `  ${key}: ${value}`);
	return {
		subject: `Demo request: ${details.vehiclePanel}`,
		text: [
			`New workflow demo request. Reply within ${RESPONSE_TIME}.`,
			``,
			`Email: ${details.email}`,
			`Vehicle / panel: ${details.vehiclePanel}`,
			`Scanner: ${details.scanner || 'not given'}`,
			`Plotter: ${details.plotter || 'not given'}`,
			`Form: ${details.source}`,
			``,
			`Attribution:`,
			...(attributionLines.length > 0 ? attributionLines : ['  none recorded'])
		].join('\n')
	};
}
